import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      // GLDN #591: a bfcache restore shows whatever count the page had when it
      // was left, so re-read the cart rather than trusting stored state.
      this.syncFromCart(null, false);
    }
  };

  /**
   * Handles the cart update event.
   *
   * GLDN #591: the bubble shows the customer-facing count from
   * `snippets/cart-bubble.liquid`, which leaves out personalizer `_tag` lines.
   * Emitters put the raw `cart.item_count` (or, from the product form, just the
   * added quantity) in `itemCount`, and that raw value is load-bearing for
   * `gldn-cart-atc-instrument.js`, so it stays raw. The bubble therefore never
   * uses `itemCount` as its number: it derives the visible count from the cart.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const { resource, data } = event.detail ?? {};
    this.syncFromCart(resource, !data?.didError, data?.itemCount);
  };

  /** Token of the newest sync, so a slow response cannot overwrite a newer one. */
  #syncToken = 0;

  /**
   * Sets the bubble to the visible count of the current cart.
   * @param {object | null | undefined} resource - The event's cart object, used when it is a full cart.
   * @param {boolean} animate - Whether to animate the bubble.
   * @param {number} [fallbackCount] - Raw count to show if the cart cannot be read.
   */
  syncFromCart = async (resource, animate, fallbackCount) => {
    const token = ++this.#syncToken;
    let count = isFullCart(resource) ? visibleItemCount(resource) : null;

    if (count === null) {
      try {
        const response = await fetch(`${Theme.routes.cart_url}.js`, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        const cart = await response.json();
        if (isFullCart(cart)) count = visibleItemCount(cart);
      } catch (_) {
        // Fall through to the fallback below.
      }
    }

    if (token !== this.#syncToken) return;
    if (count === null) {
      if (typeof fallbackCount !== 'number') return;
      count = fallbackCount;
    }

    this.renderCartBubble(count, animate);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The visible number of items in the cart (always absolute).
   * @param {boolean} [animate] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;
    this.#renderAccessibleCount(itemCount);

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    /* GLDN: Safari Private guard (Bug #3) — sessionStorage throws in iOS WebView / Safari Private */
    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(this.currentCartCount),
          timestamp: Date.now(),
        })
      );
    } catch (e) {
      console.warn('sessionStorage unavailable for key', 'cart-count', e);
    }

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Keeps the screen-reader label ("Cart count: N") in step with the visible number.
   * @param {number} itemCount - The visible number of items in the cart.
   */
  #renderAccessibleCount(itemCount) {
    const label = this.refs.cartBubbleText.querySelector('.visually-hidden');
    if (label) label.textContent = label.textContent.replace(/\d+\s*$/, String(itemCount));
  }

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    /* GLDN: Safari Private guard (Bug #3) — getItem throws in iOS WebView / Safari Private */
    let sessionStorageCount = null;
    try {
      sessionStorageCount = sessionStorage.getItem('cart-count');
    } catch (e) {
      console.warn('sessionStorage unavailable for key', 'cart-count', e);
    }

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

/**
 * Whether a value is a full cart object (as from /cart.js, /cart/change.js or
 * /cart/update.js) rather than an /cart/add.js response, which also carries
 * `items` but only the lines just added.
 * @param {any} value
 * @returns {boolean}
 */
export function isFullCart(value) {
  return Boolean(value) && typeof value.item_count === 'number' && Array.isArray(value.items);
}

/**
 * The customer-facing item count: line quantities summed, leaving out
 * personalizer add-on lines (a non-blank `_tag` property). Must match the
 * `visible_count` loop in snippets/cart-bubble.liquid.
 * @param {{ items: Array<{ quantity: number, properties?: Record<string, any> | null }> }} cart
 * @returns {number}
 */
export function visibleItemCount(cart) {
  return cart.items.reduce((sum, item) => {
    const tag = item.properties?._tag;
    // Liquid `blank`: nil, false, or an empty/whitespace-only string.
    const isTag = tag !== undefined && tag !== null && tag !== false && String(tag).trim() !== '';
    return isTag ? sum : sum + (Number(item.quantity) || 0);
  }, 0);
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
